import { supabase } from './supabase';
import { VENDOR_COLUMNS } from './vendorCatalogExport';
import { findSimilarVendor } from './vendorDedup';
import {
  makeHeaderMatcher,
  textCell,
  boolCell,
  IMPORT_HARD_CAP,
  parseCsv,
  findEncodingDamage,
  planSummary,
  rejectedRowsCsv,
} from './importCore';

// Issue #194, Session 78n10. The vendor catalog's CSV import.
//
// In plain terms: Jennifer hands back a spreadsheet — either the one Stede
// gave her, or one exported straight out of QuickBooks — and Stede works out
// which rows are new vendors, which are edits to vendors it already has, and
// which it cannot accept. Nothing is written until she has seen those numbers.
//
// Approved gate: docs/gates/Stede_VendorCatalogImport_Gate_194.html.
// Every decision below carries its D-number so the reasoning is one file away
// rather than reconstructed from the code.
//
// This module owns the VENDOR LIST — who D&R buys from. Its sibling
// vendorItemsImport.js owns the vendor's own catalog — what each of them sells.
// Both are driven by the same component through a descriptor (gate #422, M22),
// and both take their parsing, column matching and cell reading from
// importCore.js.
//
// RESTRUCTURED at Session 78s.1 and the restructure is deliberately invisible
// from outside: parseCsv, findHeaderRow, mapHeaders, findEncodingDamage,
// planSummary and rejectedRowsCsv all moved into importCore.js and are
// re-exported here unchanged. Every existing test in vendorCatalogImport.test.js
// still imports them from this file and still passes, which is the check that
// the move altered no behaviour rather than a claim that it did not.
//
// The WRITE is still the last function in the file — deliberately separated,
// because everything above it is pure and testable and the write is the only
// part that touches the database.

// Re-exported rather than redefined. A caller that has always asked this module
// for the parser keeps getting it, and there is exactly one parser.
export { parseCsv, findEncodingDamage, planSummary, rejectedRowsCsv, IMPORT_HARD_CAP };

// ── The header vocabulary ────────────────────────────────────────────────────
//
// D6 and D14. Stede's own headers match exactly, because the export writes
// them. The aliases below are what make a file that came out of QuickBooks
// Online load without a mapping step, and that is the file most likely to be
// imported first — D&R's vendors already live in Bridgette's QuickBooks.
//
// Order matters inside each list: the FIRST alias that appears in the file
// wins, so a QuickBooks export carrying both `Display Name` and `Company`
// resolves the name from Display Name. That is deliberate — DisplayName is the
// supplier's identity in QuickBooks and the string the API returns, so it is
// the one that will match at linking time.
const ALIASES = {
  name: ['vendor name', 'display name', 'company', 'vendor', 'supplier', 'supplier name', 'name'],
  contact_name: ['contact name', 'contact', 'primary contact', 'attention'],
  email: ['email', 'main email', 'e-mail', 'email address'],
  phone: ['phone', 'main phone', 'phone numbers', 'telephone', 'phone number'],
  street_address: ['street address', 'address', 'bill from street', 'street', 'address line 1', 'billing address'],
  address_line2: ['address line 2', 'address 2', 'bill from street 2', 'suite'],
  city: ['city', 'bill from city'],
  state: ['state', 'bill from state', 'province'],
  postal_code: ['zip', 'zip code', 'postal code', 'bill from zip', 'postcode'],
  payment_terms: ['payment terms', 'terms'],
  quickbooks_vendor_name: ['quickbooks name', 'quickbooks vendor name', 'qb name'],
  notes: ['notes', 'note', 'memo'],
  is_internal: ['internal', 'is internal', 'internal vendor'],
  branch_id: ['internal branch', 'branch'],
  is_active: ['active', 'is active', 'status'],
  id: ['stede id', 'id'],
};

// The QuickBooks headers that, if present, mean the file almost certainly came
// out of QuickBooks Online. Used only to say so on screen (callout K) — it
// changes no behaviour, so a false positive costs a sentence rather than data.
const QB_SIGNATURE = ['display name', 'print on check as', 'main email', 'phone numbers', 'bill from street'];

const matcher = makeHeaderMatcher(ALIASES, { signature: QB_SIGNATURE });

export const findHeaderRow = matcher.findHeaderRow;

// The generic matcher reports which of our keys the file supplied as a Set.
// This wrapper keeps the two booleans this importer has always exposed —
// hasName and hasId — so nothing that reads them has to know about the Set, and
// renames looksLikeSignature to what it actually means here.
export function mapHeaders(headerCells) {
  const info = matcher.mapHeaders(headerCells);
  return {
    ...info,
    hasName: info.found.has('name'),
    hasId: info.found.has('id'),
    looksQuickBooks: info.looksLikeSignature,
  };
}

function looksLikeEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

// ── The plan ─────────────────────────────────────────────────────────────────
//
// Everything the preview shows, computed before a single write. D5.
//
// `existing` is the current catalog (id, and every column the import can
// touch); `branches` is the branch list, so an "Internal branch" cell written
// as a NAME can be resolved back to an id. Both are passed in rather than
// fetched here, so this whole function is pure and testable.
export function buildImportPlan({ rows, headerRowIndex, mapping, existing, branches }) {
  const byId = new Map(existing.map(v => [String(v.id), v]));
  const byName = new Map();
  for (const v of existing) {
    const k = String(v.name || '').trim().toLowerCase();
    if (!byName.has(k)) byName.set(k, v);
  }
  const branchByName = new Map(
    (branches || []).map(b => [String(b.name || '').trim().toLowerCase(), b])
  );

  const editable = VENDOR_COLUMNS.filter(c => c.kind !== 'key').map(c => c.key);
  const labelFor = Object.fromEntries(VENDOR_COLUMNS.map(c => [c.key, c.header]));

  const plan = [];
  const seenIds = new Map();
  const seenNewNames = new Map();

  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const cells = rows[r];
    if (cells.every(c => String(c ?? '').trim() === '')) continue;

    // The row number the person sees is the spreadsheet's own, 1-based, so it
    // can be found by scrolling to it. Not the index in our filtered set.
    const lineNo = r + 1;
    const entry = { lineNo, errors: [], warnings: [], changes: [], values: {} };

    // Pull every mapped cell through its reader.
    const supplied = {};
    for (const [indexStr, key] of Object.entries(mapping)) {
      const raw = cells[Number(indexStr)];
      if (key === 'is_internal' || key === 'is_active') {
        const b = boolCell(raw);
        if (b && typeof b === 'object' && b.error) {
          entry.errors.push(`${labelFor[key]}: ${b.error} Use Yes or No.`);
        } else if (b !== undefined) supplied[key] = b;
      } else {
        const t = textCell(raw);
        if (t !== undefined) supplied[key] = t;
      }
    }

    // ── D2. Stede ID first, then an exact name, then it is new.
    let target = null;
    let mode = 'create';
    const rawId = supplied.id;

    if (rawId !== undefined) {
      if (!/^\d+$/.test(rawId)) {
        entry.errors.push(`Stede ID “${rawId}” is not a number. Clear the cell to add this as a new vendor.`);
      } else if (!byId.has(rawId)) {
        entry.errors.push(`Stede ID ${rawId} is not a vendor in this catalog. Clear the ID cell to add it as a new vendor, or correct it.`);
      } else {
        target = byId.get(rawId);
        mode = 'update';
        // A file listing the same vendor twice would apply two updates in an
        // order nobody chose. Reported rather than resolved by row position,
        // which is what Coupa does and what nobody can predict.
        if (seenIds.has(rawId)) {
          entry.errors.push(`Stede ID ${rawId} also appears on row ${seenIds.get(rawId)}. Remove one of them.`);
        } else seenIds.set(rawId, lineNo);
      }
    } else if (supplied.name !== undefined) {
      const hit = byName.get(supplied.name.trim().toLowerCase());
      if (hit) { target = hit; mode = 'update'; }
    }

    if (mode === 'create' && supplied.name === undefined) {
      entry.errors.push('Every vendor needs a name. This row will be skipped.');
    }

    // ── The branch cell is written as a NAME and resolved back to an id.
    if (supplied.branch_id !== undefined) {
      const b = branchByName.get(supplied.branch_id.trim().toLowerCase());
      if (!b) {
        entry.errors.push(`“${supplied.branch_id}” is not one of the branches. Leave the cell blank unless this is an internal D&R branch.`);
      } else {
        supplied.branch_id = b.id;
      }
    }

    if (supplied.email !== undefined && !looksLikeEmail(supplied.email)) {
      entry.errors.push(`“${supplied.email}” does not look like an email address.`);
    }

    // ── D3 applied. Build the write from what was SUPPLIED, never from what
    // was absent. On an update a missing key is left alone; on a create it
    // falls to the column default.
    const values = {};
    for (const key of editable) {
      if (supplied[key] === undefined) continue;
      values[key] = supplied[key];
    }
    // D15. The QuickBooks link id is never written by an import, under any
    // circumstances. It is not in VENDOR_COLUMNS, so `editable` cannot contain
    // it — this line is the assertion of that rather than the mechanism, and
    // it is here so that adding the column to the export later cannot quietly
    // make the import start writing it.
    delete values.quickbooks_vendor_id;

    if (mode === 'update') {
      for (const [key, v] of Object.entries(values)) {
        const before = target[key];
        const same = (before ?? null) === (v ?? null)
          || String(before ?? '') === String(v ?? '');
        if (!same) entry.changes.push({ key, label: labelFor[key], before, after: v });
      }
      entry.mode = entry.changes.length ? 'update' : 'unchanged';
      entry.vendorId = target.id;
      entry.vendorName = target.name;
      entry.displayName = supplied.name ?? target.name;
    } else {
      entry.mode = 'create';
      entry.displayName = supplied.name;
      if (values.is_active === undefined) values.is_active = true;
      if (values.is_internal === undefined) values.is_internal = false;

      // D7. The same duplicate check the Add vendor form runs, applied in bulk
      // — but only within the file for now; the catalog-wide check is async and
      // runs in one pass afterwards (see annotateSimilarity).
      const nameKey = String(supplied.name || '').trim().toLowerCase();
      if (nameKey && seenNewNames.has(nameKey)) {
        entry.warnings.push(`The same new vendor name appears on row ${seenNewNames.get(nameKey)}. Two vendors will be created.`);
      } else if (nameKey) seenNewNames.set(nameKey, lineNo);
    }

    // D8. An internal vendor is a D&R branch, and marking one changes how the
    // money path treats it — an order to an internal vendor goes to QuickBooks
    // as a transfer rather than as a supplier bill. Called out separately
    // because a Yes in the wrong cell has consequences three modules away.
    const becomingInternal = values.is_internal === true
      && (mode === 'create' || target.is_internal !== true);
    if (becomingInternal) {
      entry.warnings.push('Marked as an internal D&R branch. Orders to an internal vendor are treated as branch transfers, not supplier bills.');
    }

    entry.values = values;
    if (entry.errors.length) entry.mode = 'error';
    plan.push(entry);
  }

  return plan;
}

// D7's catalog-wide half, run over the vendor list already in hand rather than
// with one query per row — which is why `findSimilarVendor` was split out of
// vendorDedup.js. Warns; never blocks, same as the Add vendor form, which lets
// you save once you have looked. "Grimco" and "Grimco Supply" really can be
// two vendors.
//
// Only ACTIVE vendors are considered, which is what checkVendorSimilarity has
// always done — a deactivated vendor is not something a new one collides with.
export function annotateSimilarity(plan, existing) {
  const active = (existing || []).filter(v => v.is_active !== false);
  for (const entry of plan) {
    if (entry.mode !== 'create' || !entry.displayName) continue;
    const { matched, matchedVendor } = findSimilarVendor(
      entry.displayName, entry.values.phone || '', entry.values.email || '', active
    );
    if (matched && matchedVendor) {
      entry.warnings.push(`This looks a lot like “${matchedVendor.name}”, already in the catalog. It will be created as a separate vendor.`);
      entry.similarTo = matchedVendor.name;
    }
  }
  return plan;
}

// ── The write ────────────────────────────────────────────────────────────────
//
// D4. Good rows go in; bad rows were already held back by the planner and
// never reach here. Row by row rather than as one transaction, deliberately:
// a partial import is the intended behaviour, so a failure part way through
// leaves the rows that succeeded in place and reports the rest.
//
// Locked rule 7 asks for an RPC when multi-table writes must succeed TOGETHER.
// This is one table and must explicitly NOT be all-or-nothing, so the rule
// does not apply — noted because the shape looks like the one it governs.
//
// D9. No batch record is written. `log_inventory_vendor_changes()` already
// fires per vendor and records who and when, and because this runs through the
// app as a signed-in user, the `changed_by` failure that breaks a bulk load in
// the SQL editor cannot happen here.
export async function executeImportPlan(plan, onProgress) {
  const result = { created: 0, updated: 0, unchanged: 0, skipped: 0, failed: [] };
  let done = 0;
  const writable = plan.filter(e => e.mode === 'create' || e.mode === 'update');

  result.unchanged = plan.filter(e => e.mode === 'unchanged').length;
  result.skipped = plan.filter(e => e.mode === 'error').length;

  for (const entry of writable) {
    try {
      if (entry.mode === 'create') {
        const { error } = await supabase.from('inventory_vendors').insert(entry.values);
        if (error) throw error;
        result.created += 1;
      } else {
        const patch = Object.fromEntries(entry.changes.map(c => [c.key, c.after]));
        const { error } = await supabase
          .from('inventory_vendors').update(patch).eq('id', entry.vendorId);
        if (error) throw error;
        result.updated += 1;
      }
    } catch (e) {
      result.failed.push({ lineNo: entry.lineNo, name: entry.displayName, message: e.message || String(e) });
    }
    done += 1;
    if (onProgress) onProgress(done, writable.length);
  }

  return result;
}
